import React from 'react';
import TiptapEditor from './TiptapEditor';

interface DuplicateBookDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onDuplicate: (newData: any) => void;
  isDuplicating?: boolean;
  book: any;
}

// Helper: convert a comma-separated string into a trimmed, non-empty array
const toArray = (value: string): string[] =>
  value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const DuplicateBookDialog: React.FC<DuplicateBookDialogProps> = ({
  isOpen,
  onClose,
  onDuplicate,
  isDuplicating = false,
  book
}) => {
  const [title, setTitle] = React.useState('');
  const [subtitle, setSubtitle] = React.useState('');
  const [authors, setAuthors] = React.useState('');
  const [publisher, setPublisher] = React.useState('');
  const [language, setLanguage] = React.useState('');
  const [categories, setCategories] = React.useState('');
  const [tags, setTags] = React.useState('');
  const [description, setDescription] = React.useState('');

  // Re-initialise form whenever the dialog opens for a (possibly different) book
  React.useEffect(() => {
    if (isOpen && book) {
      setTitle(`${book.title || ''} (Bản sao)`);
      setSubtitle(book.subtitle || '');
      setAuthors(book.authors ? book.authors.join(', ') : '');
      setPublisher(book.publisher || '');
      setLanguage(book.language || '');
      setCategories(book.categories ? book.categories.join(', ') : '');
      setTags(book.tags ? book.tags.join(', ') : '');
      setDescription(book.description || '');
    }
  }, [isOpen, book]);

  if (!isOpen) return null;

  const handleDuplicate = () => {
    if (!title.trim()) {
      return;
    }

    onDuplicate({
      title: title.trim(),
      subtitle: subtitle.trim(),
      name: title.trim(),
      authors: toArray(authors),
      publisher: publisher.trim(),
      language,
      categories: toArray(categories),
      tags: toArray(tags),
      description
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[700px] shadow-lg rounded-md bg-white">
        <div className="mt-3 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sao chép sách</h3>
          <div className="space-y-4">
            {/* Form fields in 2-column layout: labels | inputs */}
            <div className="grid grid-cols-5 gap-4">
              {/* Labels Column */}
              <div className="col-span-2 space-y-4">
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Tên sách mới</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Phụ đề</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Tác giả</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Nhà xuất bản</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Ngôn ngữ</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Danh mục</div>
                <div className="text-sm font-medium text-gray-700 flex items-center h-10">Tags</div>
              </div>

              {/* Inputs Column */}
              <div className="col-span-3 space-y-4">
                <div>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Phụ đề sách (nếu có)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={authors}
                    onChange={(e) => setAuthors(e.target.value)}
                    placeholder="Nhập tác giả, phân cách bằng dấu phẩy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={publisher}
                    onChange={(e) => setPublisher(e.target.value)}
                    placeholder="Tên nhà xuất bản"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Chọn ngôn ngữ</option>
                    <option value="vi">Tiếng Việt</option>
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="de">Deutsch</option>
                    <option value="es">Español</option>
                    <option value="zh">中文</option>
                    <option value="ja">日本語</option>
                    <option value="ko">한국어</option>
                  </select>
                </div>
                <div>
                  <input
                    type="text"
                    value={categories}
                    onChange={(e) => setCategories(e.target.value)}
                    placeholder="Nhập danh mục, phân cách bằng dấu phẩy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="Nhập tags, phân cách bằng dấu phẩy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Description field spans full width */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mô tả</label>
              <TiptapEditor
                value={description}
                onChange={setDescription}
                placeholder="Nhập mô tả sách..."
                height="200px"
                showToolbar={true}
              />
              <p className="text-xs text-gray-500 mt-1">Hỗ trợ định dạng văn bản phong phú</p>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              disabled={isDuplicating}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Hủy
            </button>
            <button
              onClick={handleDuplicate}
              disabled={isDuplicating || !title.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDuplicating ? 'Đang sao chép...' : 'Sao chép'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateBookDialog;
